import React, { forwardRef } from 'react';
import {
  Card,
  ParagraphText,
  StackLayout,
  StackItem,
  HeadingText,
} from '@leagueplatform/genesis-core';
import { BodyTextHtmlTagOptions } from '#utils/body-text-html-tag-options.ts';
import { useInvokeAction } from '../../hooks/use-invoke-action.hook';
import type { MasonryNotificationBannerRendererProps } from './masonry-notification-banner-renderer.component';
import { ConditionalLinkRenderer } from '../common/conditional-link-renderer/conditional-link-renderer.component';
import { MasonryDeeplinkAction } from '../../types/masonry-driver-node-actions';

type ContentRendererProps = Omit<
  MasonryNotificationBannerRendererProps,
  'rootElementRef' | 'nodeId' | 'nodeData'
>;

export const MasonryNotificationBannerContentRenderer = forwardRef<
  HTMLAnchorElement,
  ContentRendererProps
>(({ image, imageAltText = '', heading, body, onClick }, ref) => {
  const onClickDeeplinkAction = onClick as MasonryDeeplinkAction;
  const invokeAction = useInvokeAction();

  return (
    <StackLayout
      orientation="horizontal"
      verticalAlignment="stretch"
      spacing="$one"
    >
      {image && (
        <StackItem verticalAlignment="center">
          <Card.Image
            image={image}
            imageAlt={imageAltText}
            imageLoading="lazy"
            css={{
              width: '$twoAndHalf',
              height: '$twoAndHalf',
            }}
          />
        </StackItem>
      )}
      <StackLayout spacing="$quarter">
        <HeadingText size="sm" level="2">
          <ConditionalLinkRenderer
            ref={ref}
            url={onClickDeeplinkAction?.payload.url}
            disableDefaultOnClick
            onClick={(event: React.MouseEvent) => {
              if (onClick) {
                invokeAction({
                  event,
                  ...onClick,
                });
              }
            }}
          >
            {heading}
          </ConditionalLinkRenderer>
        </HeadingText>
        <ParagraphText
          emphasis="subtle"
          htmlStringOptions={BodyTextHtmlTagOptions}
        >
          {body}
        </ParagraphText>
      </StackLayout>
    </StackLayout>
  );
});
